export type BeatType =
  | "SceneHeading"
  | "GmNarration"
  | "Dialogue"
  | "Handout"
  | "ReadAloud"
  | "ActionReceipt";

export interface SceneHeadingProps {
  title: string;
  subtitle?: string | undefined;
  locationSlug?: string | undefined;
  time?: string | undefined;
  image?: string | undefined;
}

export interface GmNarrationProps {
  text: string;
  expression?: string | undefined;
}

export interface DialogueProps {
  name: string;
  text: string;
  slug?: string | undefined;
  fileTypeSlug?: string | undefined;
  filePath?: string | undefined;
  expression?: string | undefined;
  avatarUrl?: string | undefined;
}

export interface HandoutProps {
  title?: string | undefined;
  text: string;
  style?: "letter" | "note" | "book" | "inscription" | undefined;
}

export interface ReadAloudProps {
  text: string;
}

export interface ActionReceiptChange {
  id: string;
  title: string;
  kind?: string | undefined;
  status?: string | undefined;
}

export interface ActionReceiptProps {
  count: number;
  changes: ActionReceiptChange[];
}

export type SceneHeadingBeat = { type: "SceneHeading"; props: SceneHeadingProps };
export type GmNarrationBeat = { type: "GmNarration"; props: GmNarrationProps };
export type DialogueBeat = { type: "Dialogue"; props: DialogueProps };
export type HandoutBeat = { type: "Handout"; props: HandoutProps };
export type ReadAloudBeat = { type: "ReadAloud"; props: ReadAloudProps };
export type ActionReceiptBeat = { type: "ActionReceipt"; props: ActionReceiptProps };

export type NarrationBeat =
  | SceneHeadingBeat
  | GmNarrationBeat
  | DialogueBeat
  | HandoutBeat
  | ReadAloudBeat
  | ActionReceiptBeat;

/**
 * Parses structured narration from either a ```spec fence or fallback plain text/novel markdown.
 */
export function parseNarrationBeats(source: string): NarrationBeat[] {
  if (!source || !source.trim()) return [];

  const beats: NarrationBeat[] = [];
  const fences = [...source.matchAll(/```(?:spec|beats)\s*([\s\S]*?)(```|$)/gi)];
  if (fences.length) {
    let offset = 0;
    for (const fence of fences) {
      const before = source.slice(offset, fence.index);
      if (before.trim()) beats.push(...parseNarrationBeats(before));
      for (const line of (fence[1] ?? "").split("\n").map(line => line.trim()).filter(Boolean)) {
        if (!line.startsWith("{") || !line.endsWith("}")) continue;
        try {
          const parsed = JSON.parse(line) as { type?: string; props?: Record<string, unknown> };
          if (["SceneHeading", "GmNarration", "Dialogue", "ReadAloud", "Handout"].includes(parsed.type || "") && parsed.props && typeof parsed.props === "object"
            && (parsed.type === "SceneHeading" ? typeof parsed.props.title === "string" : typeof parsed.props.text === "string")
            && (parsed.type !== "Dialogue" || typeof parsed.props.name === "string")) beats.push(parsed as unknown as NarrationBeat);
        } catch { /* Buffer incomplete beats instead of displaying private JSON. */ }
      }
      offset = (fence.index ?? 0) + fence[0].length;
    }
    const after = source.slice(offset);
    if (after.trim()) beats.push(...parseNarrationBeats(after));
    return beats;
  }

  // Fallback: Parse natural prose / novel formatting
  const paragraphs = source.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  for (const para of paragraphs) {
    // 1. Check for Scene Heading divider e.g. "─── Title ───" or "✦ ARCANIA — ... ✦"
    const headingMatch = para.match(/^[─—*✦#\s]{2,}([^─—*✦#\n]+)[─—*✦#\s]{2,}(?:\n([^\n]+))?$/);
    if (headingMatch && headingMatch[1]) {
      const title = headingMatch[1].trim();
      const subtitle = headingMatch[2]?.trim();
      beats.push({
        type: "SceneHeading",
        props: {
          title,
          subtitle: subtitle || undefined,
        },
      });
      continue;
    }

    // 2. Check for explicit Dialogue format with speaker, e.g. "นาอิ: '...'" or 'Sela: "..."'
    const speakerPrefixMatch = para.match(/^([A-Za-zก-๙\s]{2,25})[:：]\s*(["“'‘][\s\S]+["”'’])$/);
    if (speakerPrefixMatch && speakerPrefixMatch[1] && speakerPrefixMatch[2]) {
      const name = speakerPrefixMatch[1].trim();
      const text = speakerPrefixMatch[2].replace(/^["“'‘]|["”'’]$/g, "").trim();
      beats.push({
        type: "Dialogue",
        props: {
          name,
          text,
          slug: name.toLowerCase().replace(/\s+/g, "-"),
        },
      });
      continue;
    }

    // 3. Check for Handout / Letter format (e.g. blockquote > or [Letter] ...)
    if (para.startsWith(">") || para.startsWith("【") || para.startsWith("[จดหมาย]")) {
      const clean = para.replace(/^>+\s*/gm, "").replace(/^【[^】]+】\s*/, "").replace(/^\[จดหมาย\]\s*/, "");
      beats.push({
        type: "Handout",
        props: {
          text: clean,
          style: "letter",
        },
      });
      continue;
    }

    // 4. Default: Standard Narration paragraph
    beats.push({
      type: "GmNarration",
      props: {
        text: para,
      },
    });
  }

  return beats;
}

/**
 * Formats narration beats into the canonical ```spec fence format used by Craft RPG
 */
export function formatNarrationBeats(beats: readonly NarrationBeat[]): string {
  if (!beats.length) return "";
  const lines = beats.map((b) => JSON.stringify(b));
  return `\`\`\`spec\n${lines.join("\n")}\n\`\`\``;
}

export interface SpecStreamParserCallbacks {
  onBeat?: (beat: NarrationBeat) => void;
  onPostprocessingStart?: () => void;
  onProseText?: (delta: string) => void;
}

/**
 * Incremental streaming parser for spec beats and postprocessing marker
 */
export class SpecStreamParser {
  private buffer = "";
  private inSpecFence = false;
  private postprocessingStarted = false;
  private beats: NarrationBeat[] = [];
  private callbacks: SpecStreamParserCallbacks;

  constructor(callbacks: SpecStreamParserCallbacks = {}) {
    this.callbacks = callbacks;
  }

  append(delta: string): void {
    this.buffer += delta;
    this.processBuffer();
  }

  private processBuffer(): void {
    // Check for postprocessing boundary
    if (!this.postprocessingStarted && this.buffer.includes("--- STARTING POSTPROCESSING ---")) {
      this.postprocessingStarted = true;
      this.callbacks.onPostprocessingStart?.();
    }

    // Process spec fence lines
    while (true) {
      if (!this.inSpecFence) {
        const fenceStart = this.buffer.search(/```(?:spec|beats)\s*\n/i);
        if (fenceStart === -1) {
          // Send non-spec prose delta to callback if needed
          break;
        }
        const newlineIdx = this.buffer.indexOf("\n", fenceStart);
        this.buffer = this.buffer.slice(newlineIdx + 1);
        this.inSpecFence = true;
      }

      if (this.inSpecFence) {
        const lineEnd = this.buffer.indexOf("\n");
        if (lineEnd === -1) {
          // Still waiting for complete line
          break;
        }

        const line = this.buffer.slice(0, lineEnd).trim();
        this.buffer = this.buffer.slice(lineEnd + 1);

        if (line.startsWith("```")) {
          // Closing fence
          this.inSpecFence = false;
          continue;
        }

        if (line.startsWith("{") && line.endsWith("}")) {
          try {
            const parsed = JSON.parse(line) as { type?: string; props?: Record<string, unknown> };
            if (parsed.type && parsed.props && typeof parsed.props === "object") {
              const beat = parsed as unknown as NarrationBeat;
              this.beats.push(beat);
              this.callbacks.onBeat?.(beat);
            }
          } catch {
            // malformed line
          }
        }
      }
    }
  }

  finish(): NarrationBeat[] {
    this.processBuffer();
    // If buffer still has content and beats is empty, fallback parse
    if (!this.beats.length && this.buffer.trim()) {
      return parseNarrationBeats(this.buffer);
    }
    return this.beats;
  }

  getParsedBeats(): readonly NarrationBeat[] {
    return this.beats;
  }

  isPostprocessingTriggered(): boolean {
    return this.postprocessingStarted;
  }
}
